import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AuthService from "@/lib/auth/AuthApi.service";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

interface PasswordStepProps {
	email: string;
	password: string;
	onPasswordChange: (password: string) => void;
	onSubmit: (e: React.FormEvent) => void;
	onEditEmail: () => void;
	isLoading?: boolean;
}

/**
 * This component is used to enter the password for the login process.
 * It is used in the LoginPage component.
 *
 * This component supports displaying a prefilled email address from the login page,
 * which is particularly useful for invitation flows where the email is provided via URL parameter.
 * The user can then just enter the password and click on the "Weiter" button to continue.
 *
 * @param email - The email of the user.
 * @param password - The password of the user.
 * @param onPasswordChange - Function to handle password changes.
 * @param onSubmit - Function to handle form submission.
 * @param onEditEmail - Function to go back to the email step.
 * @param isLoading - Whether the form is in a loading state.
 */
export const PasswordStep = ({
	email,
	password,
	onPasswordChange,
	onSubmit,
	onEditEmail,
	isLoading = false,
}: PasswordStepProps) => {
	const [showPassword, setShowPassword] = useState(false);

	return (
		<form onSubmit={onSubmit} className="space-y-6">
			<h1 className="text-center text-2xl font-semibold text-foreground">
				Passwort eingeben
			</h1>

			<div className="space-y-2">
				<label className="text-sm text-muted-foreground" htmlFor="email">
					Email-Adresse
				</label>
				<div className="relative">
					<Input
						id="email"
						type="email"
						value={email}
						disabled
						className="w-full"
					/>
					<Button
						variant="plain"
						onClick={onEditEmail}
						className="absolute right-0 top-1/2 -translate-y-1/2 text-primary hover:text-primary/80"
						disabled={isLoading}
					>
						Bearbeiten
					</Button>
				</div>
			</div>

			<div className="space-y-2">
				<label className="text-sm text-muted-foreground" htmlFor="password">
					Passwort
				</label>
				<div className="relative">
					<Input
						id="password"
						type={showPassword ? "text" : "password"}
						value={password}
						onChange={(e) => onPasswordChange(e.target.value)}
						placeholder="Passwort"
						className="w-full"
						autoFocus
						disabled={isLoading}
					/>
					<Button
						variant="plain"
						size="icon"
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground"
						disabled={isLoading}
					>
						{showPassword ? (
							<EyeOff className="h-5 w-5" />
						) : (
							<Eye className="h-5 w-5" />
						)}
					</Button>
				</div>
				<Link
					to="/auth/login"
					onClick={() =>
						AuthService.getInstance().passwordChangeRequest({
							email,
						})
					}
					className={`block text-sm hover:underline ${
						isLoading ? "pointer-events-none opacity-50" : ""
					}`}
				>
					Passwort vergessen?
				</Link>
			</div>

			<Button type="submit" className="w-full" size="lg" disabled={isLoading}>
				{isLoading ? (
					<>
						<Loader2 className="h-4 w-4 animate-spin mr-2" />
						Anmelden...
					</>
				) : (
					"Weiter"
				)}
			</Button>
		</form>
	);
};
